import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { fetchPlayerProfile } from '../../api/playerProfile';
import { fetchPlayerSeasonStats } from '../../api/playerMetrics';
import PlayerMetricsBio from './PlayerMetricsBio';
import PlayerMetricsSeasonStats from './PlayerMetricsSeasonStats';
import PlayerMetricsGameLog from './PlayerMetricsGameLog';
import PlayerMetricsSplits from './PlayerMetricsSplits';
import PlayerMetricsRadar from './PlayerMetricsRadar';

const { FiX, FiUser, FiBarChart2, FiList, FiActivity, FiRefreshCw, FiShield } = FiIcons;

const TABS = [
  { id: 'overview', label: 'Scouting', icon: FiShield },
  { id: 'season', label: 'Season', icon: FiBarChart2 },
  { id: 'gamelog', label: 'Game Log', icon: FiList },
  { id: 'splits', label: 'Splits', icon: FiActivity },
];

const PlayerMetricsModal = ({ playerId, playerName, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const load = () => {
    if (!playerId) return;
    setLoading(true);
    setError(null);
    Promise.all([fetchPlayerProfile(playerId), fetchPlayerSeasonStats(playerId)])
      .then(([profileData, metricsData]) => {
        setProfile(profileData);
        setMetrics(metricsData);
      })
      .catch(() => setError('Could not load player metrics.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [playerId]);

  const isPitcher = profile?.player?.primaryPosition?.type === 'Pitcher';

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
        <motion.div 
          className="relative w-full max-w-3xl bg-[#1e3a29] border-[6px] border-[#d8ceb3] shadow-[0_0_50px_rgba(0,0,0,0.5)] z-10 max-h-[96vh] flex flex-col overflow-hidden"
          initial={{ scale: 0.9, y: 40 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 40 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        >
          {/* Top Bar */}
          <div className="bg-[#386646] border-b-2 border-[#658d71] p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded bg-[#1e3a29] border border-[#d8ceb3] flex items-center justify-center">
                <SafeIcon icon={isPitcher ? FiActivity : FiShield} size={20} className="text-[#d8ceb3]" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#a9c9b1] font-black">Scouting Report</p>
                <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tighter leading-none">
                  {playerName}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} disabled={loading} className="p-2.5 border border-[#658d71] text-[#a9c9b1] hover:bg-[#658d71] transition-colors">
                <SafeIcon icon={FiRefreshCw} size={16} className={loading ? 'animate-spin' : ''} />
              </button>
              <button onClick={onClose} className="p-2.5 border border-[#658d71] text-[#d8ceb3] hover:bg-[#e85555] hover:text-white transition-colors">
                <SafeIcon icon={FiX} size={20} />
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex border-b-2 border-[#658d71] shrink-0 bg-[#2d4d38] overflow-x-auto">
            {TABS.map(tab => (
              <button 
                key={tab.id} 
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3.5 text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border-r border-[#658d71] last:border-0 ${
                  activeTab === tab.id 
                    ? 'bg-[#1e3a29] text-[#d8ceb3] border-b-4 border-b-[#d8ceb3] -mb-[2px]' 
                    : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
                }`}
              >
                <SafeIcon icon={tab.icon} size={12} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-[#1e3a29]">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-4">
                <div className="w-10 h-10 border-4 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
                <span className="text-[#a9c9b1] text-xs font-black tracking-[0.3em] uppercase">Hydrating Metrics...</span>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-[#e85555] text-sm font-black uppercase tracking-widest">{error}</p>
                <button onClick={load} className="mt-4 px-6 py-2 border border-[#658d71] text-[#a9c9b1] hover:text-white uppercase text-[10px] font-black">Retry</button>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeTab}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col gap-8 pb-12"
                >
                  {activeTab === 'overview' && (
                    <>
                      <PlayerMetricsBio player={profile?.player} />
                      <PlayerMetricsRadar profile={profile} isPitcher={isPitcher} />
                    </>
                  )}
                  {activeTab === 'season' && (
                    <PlayerMetricsSeasonStats metrics={metrics} profile={profile} isPitcher={isPitcher} />
                  )}
                  {activeTab === 'gamelog' && (
                    <PlayerMetricsGameLog metrics={metrics} isPitcher={isPitcher} />
                  )}
                  {activeTab === 'splits' && (
                    <PlayerMetricsSplits metrics={metrics} isPitcher={isPitcher} />
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {/* Footer Branding */}
          <div className="bg-[#1e3a29] border-t border-[#658d71] p-3 flex justify-between items-center shrink-0">
            <span className="text-[8px] font-black text-[#658d71] uppercase tracking-[0.4em]">Official MLB Stats API Pipeline</span>
            <div className="flex gap-2">
              <div className="w-2 h-2 rounded-full bg-[#658d71]" />
              <div className="w-2 h-2 rounded-full bg-[#d8ceb3]" />
              <div className="w-2 h-2 rounded-full bg-[#e85555]" />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PlayerMetricsModal;